"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_RELEASE_MAX_STORED = exports.PERMISSION_SNAPSHOT_KEY_PREFIX = void 0;
exports.isStoredPermissionVaultRecord = isStoredPermissionVaultRecord;
const partition_index_1 = require("./partition-index");
/** Storage key prefixes of the permission vault, one per record kind. */
exports.PERMISSION_SNAPSHOT_KEY_PREFIX = {
    partition: 'permission-snapshot:v3:',
    pointer: 'permission-snapshot-pointer:v3:',
    pendingOwner: 'permission-snapshot-pending-owner:v2:',
    /** The App release confirmations of one person on one server. */
    appRelease: 'app-release-snapshot:v1:',
};
/** At most this many App Containers keep a confirmation per person. */
exports.APP_RELEASE_MAX_STORED = 500;
const MAX_PENDING_LOCAL_OWNER_GRANTS = 1000;
function isRecord(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}
function isNonEmptyString(value) {
    return typeof value === 'string' && value.length > 0;
}
function isFiniteNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
}
function isNonNegativeInteger(value) {
    return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}
function isPartitionIndex(value) {
    return (isNonNegativeInteger(value) && value < partition_index_1.PERMISSION_SNAPSHOT_PARTITION_COUNT);
}
function isSignedEnvelopeShape(value) {
    if (!isRecord(value) || value.version !== 1 || value.alg !== 'ES256') {
        return false;
    }
    return (isNonEmptyString(value.kid) &&
        isNonEmptyString(value.signature) &&
        isRecord(value.payload) &&
        isNonEmptyString(value.payload.issuedAt) &&
        isNonEmptyString(value.payload.expiresAt));
}
function isStoredSignedEnvelope(value) {
    return (isRecord(value) &&
        isFiniteNumber(value.cachedAt) &&
        isSignedEnvelopeShape(value.envelope));
}
function isStoredPermissionPartition(value) {
    return (value.version === 3 &&
        isPartitionIndex(value.partitionIndex) &&
        Array.isArray(value.base) &&
        value.base.every(isStoredSignedEnvelope) &&
        Array.isArray(value.overlays) &&
        value.overlays.length <= partition_index_1.PERMISSION_SNAPSHOT_MAX_OVERLAYS &&
        value.overlays.every(isStoredSignedEnvelope) &&
        (value.baseRefreshedAt === null || isFiniteNumber(value.baseRefreshedAt)));
}
function isStoredPermissionPointer(value) {
    return (value.version === 3 &&
        isNonEmptyString(value.tenantId) &&
        isNonNegativeInteger(value.confirmedPermissionGraphVersion) &&
        Array.isArray(value.partitions) &&
        value.partitions.length <= partition_index_1.PERMISSION_SNAPSHOT_PARTITION_COUNT &&
        value.partitions.every(isPartitionIndex) &&
        new Set(value.partitions).size === value.partitions.length &&
        isRecord(value.staleKeys) &&
        Object.values(value.staleKeys).every(isNonNegativeInteger) &&
        isNonNegativeInteger(value.staleBelowVersion));
}
function isStoredPendingLocalOwnerGrants(value) {
    return (value.version === 3 &&
        Array.isArray(value.grants) &&
        value.grants.length <= MAX_PENDING_LOCAL_OWNER_GRANTS &&
        value.grants.every(grant => isRecord(grant) &&
            isNonEmptyString(grant.resourceId) &&
            (grant.resourceType === 'doc' || grant.resourceType === 'folder') &&
            isNonEmptyString(grant.localMutationId) &&
            isFiniteNumber(grant.createdAt) &&
            isFiniteNumber(grant.expiresAt)));
}
function isStoredAppReleases(value) {
    if (value.version !== 1 || !isRecord(value.releases))
        return false;
    const releases = Object.entries(value.releases);
    return (releases.length <= exports.APP_RELEASE_MAX_STORED &&
        releases.every(([key, release]) => /^[^/]+\/[^/]+$/.test(key) && isStoredSignedEnvelope(release)));
}
/**
 * Structural check of a stored permission vault record by its key prefix.
 * Unknown prefixes fail closed. Signatures are verified by the web store.
 */
function isStoredPermissionVaultRecord(key, value) {
    if (!isRecord(value))
        return false;
    if (key.startsWith(exports.PERMISSION_SNAPSHOT_KEY_PREFIX.partition)) {
        return isStoredPermissionPartition(value);
    }
    if (key.startsWith(exports.PERMISSION_SNAPSHOT_KEY_PREFIX.pointer)) {
        return isStoredPermissionPointer(value);
    }
    if (key.startsWith(exports.PERMISSION_SNAPSHOT_KEY_PREFIX.pendingOwner)) {
        return isStoredPendingLocalOwnerGrants(value);
    }
    if (key.startsWith(exports.PERMISSION_SNAPSHOT_KEY_PREFIX.appRelease)) {
        return isStoredAppReleases(value);
    }
    return false;
}
//# sourceMappingURL=stored-records.js.map